import classnames from 'classnames';
import React from 'react';

export interface DateObject {
  month: string;
  day: string;
}

export interface DateRange {
  startDate: DateObject;
  endDate: DateObject;
}

interface MultipleDates {
  multipleDates: (DateObject | DateRange)[];
}

export type DateType = DateObject | DateRange | MultipleDates;

const Constants = {
  maxDisplayDates: 1
};

const DateTag: React.FC<{
  dates: DateType;
  variant?: 'default' | 'large' | 'slim';
  hasCustomDuration?: boolean | false;
}> = ({ dates, variant = 'default', hasCustomDuration }) => {
  const renderSingleDate = (date: DateObject) => (
    <p>
      <span
        className={classnames({
          'inline-flex': variant === 'slim',
          'text-2-fixed-l inline-flex md:block': variant === 'large',
          block: variant === 'default'
        })}
      >
        {date.month}
      </span>{' '}
      <span
        className={classnames({
          'text-3-fixed-l': variant === 'large',
          'inline-block': variant === 'slim'
        })}
      >
        {date.day}
      </span>
    </p>
  );

  const renderSingleDateWithCustomDuration = (startDateLabel: string) => (
    <p>
      <span
        className={classnames({
          'text-2-fixed-l block': variant === 'large',
          'inline-flex md:block': variant !== 'large'
        })}
      >
        Starts
      </span>

      <span
        className={classnames({
          'text-3-fixed-l': variant === 'large'
        })}
      >
        {startDateLabel}
      </span>
    </p>
  );

  const renderDateRange = (range: DateRange) => (
    <>
      {range.startDate.month} {range.startDate.day}
      {variant !== 'slim' && <br />} - {range.endDate.month} {range.endDate.day}
    </>
  );

  // helper: formats "MMM DD" from any DateType (and handles "16-20" -> "16")
  const getStartDateLabel = (d: DateType): string | null => {
    const format = (obj: DateObject) => {
      const firstDay = obj.day.split(/[-–—]/)[0].trim(); // "16-20" -> "16", "16" -> "16"
      return `${obj.month} ${firstDay}`;
    };

    if ('month' in d && 'day' in d) return format(d);

    if ('startDate' in d) return format(d.startDate);

    if ('multipleDates' in d) {
      const first = d.multipleDates[0];
      if (!first) return null;

      if ('month' in first && 'day' in first) return format(first);
      if ('startDate' in first) return format(first.startDate);
    }

    return null;
  };

  const renderMultipleDates = (multipleDatesArg: MultipleDates) => {
    const { multipleDates } = multipleDatesArg;

    const firstDate = multipleDates.slice(0, Constants.maxDisplayDates);
    return (
      <>
        {firstDate.map((date, index) => {
          if ('month' in date && 'day' in date) {
            return (
              <p key={index}>
                {date.month} {date.day}
              </p>
            );
          }
          if ('startDate' in date && 'endDate' in date) {
            return (
              <p key={index}>
                {date.startDate.month} {date.startDate.day}
                {variant !== 'slim' && <br />} - {date.endDate.month} {date.endDate.day}
              </p>
            );
          }
        })}
        {multipleDates.length > Constants.maxDisplayDates && (
          <p className="text-white/60">+{multipleDates.length - Constants.maxDisplayDates} more</p>
        )}
      </>
    );
  };

  let content = null;

  if (hasCustomDuration) {
    const start = getStartDateLabel(dates);
    content = start ? renderSingleDateWithCustomDuration(start) : null;
  } else if ('month' in dates) {
    content = renderSingleDate(dates);
  } else if ('startDate' in dates && 'endDate' in dates) {
    content = renderDateRange(dates);
  } else if ('multipleDates' in dates) {
    content = renderMultipleDates(dates);
  }

  const hasMultipleDates = 'multipleDates' in dates;

  return (
    <div
      className={classnames(
        'text-2-fixed-medium flex min-w-[72px] flex-col items-center justify-center bg-black/30 text-center text-white backdrop-blur',
        {
          'h-12 min-h-12 w-full min-w-max px-4 md:min-h-[72px] md:min-w-[72px] md:px-0':
            variant === 'default',
          'rounded-lg': variant === 'large',
          [`px-3 ${hasMultipleDates ? 'py-2' : 'py-4'}`]: variant === 'slim',
          'items-start rounded md:items-center': variant === 'slim' || variant === 'default',
          'size-full min-h-[72px]': variant === 'large' || variant === 'default'
        }
      )}
    >
      {content}
    </div>
  );
};

export default DateTag;
